import { useState, useEffect } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import styles from './LoadScheduleModal.module.css';  // Eigenes CSS-Modul
import { toast } from 'react-toastify';
import ApiClient from '../utils/apiClient';
import type { ApiError } from '../utils/apiClient';
import ConfirmModal from '../components/ConfirmModal';

/** Gespeicherter Stundenplan aus GET /api/classes/{id}/schedules. */
interface StoredSchedule {
  id: string;
  name?: string | null;
  description?: string | null;
  is_primary?: boolean | null;
  updated_at?: string | null;
  [key: string]: unknown;
}

/** Rohdaten, die beim Laden an den Aufrufer (App/TeacherSchedulingProcess) gehen. */
export interface LoadedScheduleData {
  rawScheduleTimes: Record<string, unknown>;
  members: unknown[];
  name: string;
  id: string;
  is_primary: boolean;
}

interface LoadScheduleModalProps {
  onLoad: (data: LoadedScheduleData) => void;
  onClose: () => void;
  classId: string;
  onSchedulesChanged?: () => void;
}

// I4: onSchedulesChanged (optional) invalidiert den Schedule-Cache nach Delete/Primary-Toggle.
// Bei Verwendung außerhalb des SchedulesCacheProvider (z. B. in App.js) bleibt es ungesetzt = no-op.
export default function LoadScheduleModal({ onLoad, onClose, classId, onSchedulesChanged }: LoadScheduleModalProps) {
  const [schedules, setSchedules] = useState<StoredSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<{ id: string; name?: string | null } | null>(null);

  // Escape-Listener
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    
    window.addEventListener('keydown', handleEscape);
    
    // Cleanup beim Unmount
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const response = await ApiClient.get(`/classes/${classId}/schedules`);
        // ApiClient wirft bei !ok - kein manueller Check nötig
        const data = await response.json();
        setSchedules(data.schedules);
      } catch (caught) {
        const error = caught as ApiError;
        if (error.name !== 'AbortError') {
          setError(error.originalErrorData?.detail || error.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, [classId]);

  const handleLoadSchedule = async (scheduleId: string) => {
    try {
        setLoading(true);
        setError(null); // Fehler zurücksetzen

        // Hole Schedule-Details und Klassenmitglieder parallel
        // ApiClient wirft bei !ok - kein manueller Check nötig
        const [scheduleResponse, studentsResponse] = await Promise.all([
            ApiClient.get(`/schedules/${scheduleId}`),
            ApiClient.get(`/classes/${classId}/members`)
        ]);

        const scheduleDataFromApi = await scheduleResponse.json();
        const studentsData = await studentsResponse.json();

        // Bereite Daten für die Übergabe vor (Rohdaten)
        const dataToPass: LoadedScheduleData = {
            // Rohdaten des Stundenplans direkt aus der API
            rawScheduleTimes: scheduleDataFromApi.available_times || {},
            // Klassenmitglieder direkt aus der API
            members: studentsData.members || [],
            // Name des Stundenplans
            name: scheduleDataFromApi.name || '',
            // ID des Stundenplans
            id: scheduleId,
            // Primary-Status des Stundenplans
            is_primary: scheduleDataFromApi.is_primary || false
        };

        console.log('Loading schedule with name:', dataToPass.name);
        console.log('Data to pass to App:', dataToPass); // Angepasstes Log

        // Rufe onLoad mit den Rohdaten auf
        onLoad(dataToPass);
        onClose(); // Modal schließen

    } catch (caught) {
        const e = caught as ApiError;
        // Setze den Fehler im State, damit er im Modal angezeigt wird
        setError(`Laden fehlgeschlagen: ${e.message}`);
        console.error('Error loading schedule:', e);
        toast.error(`Laden fehlgeschlagen: ${e.message}`); // Zeige Toast-Fehler
    } finally {
        setLoading(false);
    }
  };

  const handleDelete = async (scheduleId: string, scheduleName: string | null | undefined, e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // Verhindert das Laden beim Löschen
    
    // Statt window.confirm das ConfirmModal verwenden
    setScheduleToDelete({ id: scheduleId, name: scheduleName });
    setShowDeleteConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (!scheduleToDelete) return;

    try {
      await ApiClient.delete(`classes/${classId}/schedules/${scheduleToDelete.id}`);
      // ApiClient wirft bei !ok - kein manueller Check nötig

      // Aktualisiere die Liste der Stundenpläne
      setSchedules(schedules.filter(schedule => schedule.id !== scheduleToDelete.id));
      toast.success('Stundenplan erfolgreich gelöscht');

      // Modal schließen und Zustand zurücksetzen
      setShowDeleteConfirmModal(false);
      setScheduleToDelete(null);
      onSchedulesChanged?.(); // I4: Schedule-Cache invalidieren

    } catch (caught) {
      const error = caught as ApiError;
      console.error('Error deleting schedule:', error);
      toast.error(error.originalErrorData?.detail || error.message || 'Fehler beim Löschen des Stundenplans');

      // Modal schließen und Zustand zurücksetzen
      setShowDeleteConfirmModal(false);
      setScheduleToDelete(null);
    }
  };


  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.content}>
          <h2>Gespeicherte Stundenpläne</h2>
          
          {/* Zeige Lade- und Fehlerzustände prominenter */}
          {loading && <p className={styles.loadingMessage}>Lade Stundenpläne...</p>}
          {error && !loading && <p className={styles.error}>Fehler: {error}</p>}
          
          <div className={styles.scheduleList}>
            {!loading && !error && schedules.length === 0 ? (
              <p className={styles.noSchedules}>
                Keine gespeicherten Stundenpläne vorhanden.
              </p>
            ) : (
              schedules.map((schedule) => (
                <div key={schedule.id} className={`${styles.scheduleItem} ${schedule.is_primary ? styles.primarySchedule : ''}`}>
                  <div className={styles.scheduleInfo}>
                    <div className={styles.scheduleHeader}>
                      <h3>{schedule.name}</h3>
                      {schedule.is_primary && (
                        <span className={styles.primaryBadge} title="Dieser Plan ist für Studierende freigegeben">Freigegeben</span>
                      )}
                    </div>
                    {schedule.description && <p>{schedule.description}</p>}
                    <small>
                      Zuletzt geändert: {schedule.updated_at ? new Date(schedule.updated_at).toLocaleString('de-DE') : '-'}
                    </small>
                  </div>
                  <div className={styles.buttonGroup}>
                    <button
                      onClick={(e) => handleDelete(schedule.id, schedule.name, e)}
                      className={styles.deleteButton}
                      title="Stundenplan löschen"
                      disabled={loading} // Deaktiviere während des Ladens
                    >
                      🗑️
                    </button>
                    <button
                      onClick={() => handleLoadSchedule(schedule.id)}
                      className={styles.loadButton}
                      disabled={loading} // Deaktiviere während des Ladens
                    >
                      Laden
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className={styles.buttons}>
            <button onClick={onClose} disabled={loading}>Schließen</button>
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && scheduleToDelete && (
        <ConfirmModal
          isOpen={showDeleteConfirmModal}
          onClose={() => {
            setShowDeleteConfirmModal(false);
            setScheduleToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Stundenplan löschen"
          message={`Möchtest Du den Stundenplan "${scheduleToDelete.name}" wirklich löschen?`}
          buttonText="Löschen"
        />
      )}
    </div>
  );
} 